import React, { useMemo } from "react";
import { FileText, MailCheck, Package, ExternalLink } from "lucide-react";

/**
 * iter72f · VendorResponsesPanel
 *
 * Club-side card summarizing every vendor submission that came back
 * through a Secure Vendor Link (`/v/{token}`). Reads two spots on the
 * order doc, both populated by `POST /public/vendor-link/{token}/submit`:
 *   • order.items[i].vendor_response — { quantity, vendor_notes,
 *       submitted_at, submitted_by_email, submitted_via_token_id }
 *   • order.vendor_response_files — [{ doc_id, photo_id, filename,
 *       content_type, size, uploaded_at, submitted_via_token_id }]
 *
 * Groups edits + files by submission (token_id) so if the same vendor
 * (or multiple vendors) submits several times, each cycle renders as its
 * own card. Latest submission on top.
 *
 * If nothing has come back yet, the panel hides itself completely — we
 * don't want to advertise an empty state on every order.
 */
export default function VendorResponsesPanel({ order }) {
  const groups = useMemo(() => _groupResponses(order), [order]);
  if (!groups.length) return null;

  return (
    <section
      data-testid="vendor-responses-panel"
      className="surface-card p-6"
    >
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="overline text-[#B59A5A]">Vendor Responses</div>
          <h3 className="font-serif text-2xl text-[#1A422D]">
            {groups.length === 1 ? "1 submission" : `${groups.length} submissions`}
          </h3>
        </div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#7A7A7A] inline-flex items-center gap-2">
          <MailCheck size={12} /> via Secure Vendor Link
        </div>
      </div>

      <div className="mt-6 space-y-5" data-testid="vendor-responses-list">
        {groups.map((g) => (
          <SubmissionCard key={g.token_id || g.submitted_at} group={g} />
        ))}
      </div>
    </section>
  );
}

function SubmissionCard({ group }) {
  const submitted = _formatTs(group.submitted_at);
  return (
    <div
      className="border border-[#E5E3D8] bg-[#FBF9F1] p-5"
      data-testid={`vendor-response-${group.token_id || "unknown"}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-serif text-lg text-[#1A422D]">
            {group.submitted_by_email || "Vendor"}
          </div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#7A7A7A] mt-0.5">
            Submitted {submitted}
          </div>
        </div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#8a6f33] inline-flex items-center gap-2">
          <Package size={12} /> {group.line_edits.length} line
          {group.line_edits.length === 1 ? "" : "s"} · {group.files.length} file
          {group.files.length === 1 ? "" : "s"}
        </div>
      </div>

      {group.line_edits.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm border border-[#E5E3D8] bg-white">
            <thead className="bg-[#F3F1E9]">
              <tr className="text-left">
                <Th>Line Item</Th>
                <Th className="text-right">Requested</Th>
                <Th className="text-right">Vendor Qty</Th>
                <Th>Notes</Th>
              </tr>
            </thead>
            <tbody>
              {group.line_edits.map((row) => (
                <tr
                  key={row.line_id}
                  className="border-t border-[#E5E3D8] align-top"
                  data-testid={`vr-line-${row.line_id}`}
                >
                  <Td>
                    <div className="font-serif text-[#1A422D]">{row.item_name || "Line"}</div>
                    {row.brand && (
                      <div className="text-[10px] uppercase tracking-[0.18em] text-[#7A7A7A]">
                        {row.brand}
                      </div>
                    )}
                  </Td>
                  <Td className="text-right font-mono">{row.requested_quantity ?? "—"}</Td>
                  <Td className="text-right font-mono">
                    <VendorQty
                      requested={row.requested_quantity}
                      vendor={row.vendor_quantity}
                    />
                  </Td>
                  <Td>
                    {row.vendor_notes ? (
                      <span className="text-[#1A1A1A]">{row.vendor_notes}</span>
                    ) : (
                      <span className="text-[#7A7A7A]">—</span>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {group.files.length > 0 && (
        <div className="mt-4">
          <div className="overline text-[#B59A5A] mb-2">Attached documents</div>
          <ul className="space-y-1 text-sm" data-testid="vr-file-list">
            {group.files.map((f) => (
              <li key={f.doc_id || f.photo_id} className="flex items-center gap-2">
                <FileText size={14} className="text-[#8a6f33] shrink-0" />
                <a
                  href={`/api/photos/${f.photo_id || f.doc_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#1A422D] hover:underline inline-flex items-center gap-1 min-w-0"
                >
                  <span className="truncate">{f.filename || "Attachment"}</span>
                  <ExternalLink size={11} className="shrink-0 opacity-60" />
                </a>
                <span className="text-[10px] text-[#7A7A7A] shrink-0">
                  {_formatSize(f.size)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function VendorQty({ requested, vendor }) {
  if (vendor == null) return <span className="text-[#7A7A7A]">—</span>;
  if (requested == null) return <span>{vendor}</span>;
  const diff = Number(vendor) - Number(requested);
  const color =
    diff < 0
      ? "text-[#8a1a1a]"
      : diff > 0
        ? "text-[#8a6f33]"
        : "text-[#1A422D]";
  return (
    <span className={color}>
      {vendor}
      {diff !== 0 && (
        <span className="text-[10px] ml-1 uppercase tracking-[0.16em]">
          ({diff > 0 ? "+" : ""}
          {diff})
        </span>
      )}
    </span>
  );
}

function Th({ children, className = "" }) {
  return (
    <th
      className={`px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-[#7A7A7A] ${className}`}
    >
      {children}
    </th>
  );
}
function Td({ children, className = "" }) {
  return <td className={`px-3 py-2 ${className}`}>{children}</td>;
}

// ---- helpers ----

function _groupResponses(order) {
  if (!order) return [];
  const files = order.vendor_response_files || [];
  const lineEdits = [];
  for (const li of order.items || []) {
    const vr = li.vendor_response;
    if (!vr) continue;
    lineEdits.push({
      line_id: li.line_id,
      item_name: li.item_name,
      brand: li.brand,
      requested_quantity: li.quantity,
      vendor_quantity: vr.quantity,
      vendor_notes: vr.vendor_notes,
      submitted_at: vr.submitted_at,
      submitted_by_email: vr.submitted_by_email,
      submitted_via_token_id: vr.submitted_via_token_id,
    });
  }
  if (!lineEdits.length && !files.length) return [];

  // Group by token_id so re-submissions render as separate cards. Any
  // items or files missing a token_id fall into a synthetic "legacy"
  // bucket (unusual but survivable — old data or manual imports).
  const byToken = new Map();
  const ensure = (id, seedTs, seedEmail) => {
    if (!byToken.has(id)) {
      byToken.set(id, {
        token_id: id,
        submitted_at: seedTs || null,
        submitted_by_email: seedEmail || null,
        line_edits: [],
        files: [],
      });
    }
    return byToken.get(id);
  };
  for (const e of lineEdits) {
    const g = ensure(e.submitted_via_token_id || "legacy", e.submitted_at, e.submitted_by_email);
    g.line_edits.push(e);
    if (!g.submitted_at || e.submitted_at > g.submitted_at) g.submitted_at = e.submitted_at;
    if (!g.submitted_by_email && e.submitted_by_email) g.submitted_by_email = e.submitted_by_email;
  }
  for (const f of files) {
    const g = ensure(f.submitted_via_token_id || "legacy", f.uploaded_at, null);
    g.files.push(f);
    if (!g.submitted_at || (f.uploaded_at || "") > g.submitted_at) g.submitted_at = f.uploaded_at;
  }
  return Array.from(byToken.values()).sort(
    (a, b) => (b.submitted_at || "").localeCompare(a.submitted_at || "")
  );
}

function _formatTs(iso) {
  if (!iso) return "just now";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function _formatSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
